import { useCallback } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { includes } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { getLabelMessage } from '@gef-ui/localization/messages';
import ModalLoader from '@gef-ui/material/components/ModalLoader';
import ArrowDownToBracket from '@gef-ui/material-icons/ArrowDownToBracket';
import CircleInfo from '@gef-ui/material-icons/CircleInfo';
import EnvelopeOpenText from '@gef-ui/material-icons/EnvelopeOpenText';
import Print from '@gef-ui/material-icons/Print';
import ScrollRegular from '@gef-ui/material-icons/ScrollRegular';
import Share from '@gef-ui/material-icons/Share';
import TimeInterruption from '@gef-ui/material-icons/TimeInterruption';
import Wallet from '@gef-ui/material-icons/Wallet';

import { codelistProvider, ComponentLoader } from 'components';
import ProspectorButton from 'components/atoms/GefMui/ProspectorButton';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import {
	MPU_ADAPT_TASK,
	MPU_ASSUME_TASK_OUTOFHIERARCHY,
	MPU_FORWARD_TASK,
	MPU_PROCESSED_TASK,
	MPU_SEND_TASK_BY_MAIL,
} from 'features/common/acl/constants';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { fetchHistory, postTakeTask } from 'features/views/taskDetail/actions';
import {
	TAKE_TASK_ALREADY_PROCESSED_ERR_MSG,
	TAKE_TASK_USER_HAS_NO_ACCESS_ERR_MSG,
} from 'features/views/taskDetail/constants';
import { getDetail, getHistory, getSolverIsLoggedUser } from 'features/views/taskDetail/selectors';
import taskFeedback from 'features/views/taskFeedback';
import taskForwarding from 'features/views/taskForwarding';
import taskPartnerSearch from 'features/views/taskPartnerSearch';
import { getCodelistPropById, getRoutePath } from 'utils';
import { hasPermissionByIdsAndIncludedValuesWithAllKeywordCheck } from 'utils/permissionResolvers';

export const mpuAssumeTaskOutofhierarchyId = MPU_ASSUME_TASK_OUTOFHIERARCHY[0].id;

export const SubheaderToolbar = ({ taskId, types }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const taskDetail = useSelector(getDetail);
	const solverIsLoggedUser = useSelector(getSolverIsLoggedUser);
	const taskHistory = useSelector(getHistory);

	const taskPolicyPartnerListFetchStorePath = `${fetch.constants.urls.taskPolicyPartnerList}`;

	const mpuAssumeTaskOutofhierarchy = useSelector((state) =>
		hasPermissionByIdsAndIncludedValuesWithAllKeywordCheck(state)([
			{
				id: mpuAssumeTaskOutofhierarchyId,
				attributes: [
					{
						key: 'taskTypes',
						value: [String(taskDetail.taskTypeId)],
					},
				],
			},
		]),
	);

	const mpuAdaptTask = useSelector((state) => hasPermissionByIds(state)(MPU_ADAPT_TASK));
	const mpuProcessTask = useSelector((state) => hasPermissionByIds(state)(MPU_PROCESSED_TASK));
	const mpuForwardTask = useSelector((state) => hasPermissionByIds(state)(MPU_FORWARD_TASK));
	const mpuSendTaskByMail = useSelector((state) => hasPermissionByIds(state)(MPU_SEND_TASK_BY_MAIL));

	const taskPolicyPartnerListFetchState = useSelector(
		fetch.selectors.getFetchState(taskPolicyPartnerListFetchStorePath),
	);

	const handleTakeButton = useCallback(() => {
		const { taskTypeId } = taskDetail;

		NiceModal.show(MODALS.lightbox_modal, {
			title: getLabelMessage('lightBox:headerAssignment'),
			content: getLabelMessage('lightBox:messageAssignment'),
			onConfirm: () => dispatch(postTakeTask(taskId, taskTypeId)),
		});
	}, [dispatch, taskDetail, taskId]);

	const handleProcessInformation = useCallback(() => {
		const { taskTypeId } = taskDetail;

		dispatch(taskFeedback.actions.postFeedback(taskId, taskTypeId, taskFeedback.constants.FINISH_ID_GET_INFO));
	}, [dispatch, taskDetail, taskId]);

	const handleTaskforward = useCallback(() => {
		dispatch(taskForwarding.actions.setTasks([taskDetail]));
		dispatch(dialog.actions.clear());
		NiceModal.show(MODALS.task_forwarding);
	}, [dispatch, taskDetail]);

	const handleReloadTaskHistory = useCallback(() => {
		dispatch(fetchHistory(taskDetail.id));
	}, [dispatch, taskDetail.id]);

	const handleHistoryButton = useCallback(() => {
		NiceModal.show(MODALS.lead_task_history, {
			historyItems: taskHistory,
			onReloadFn: handleReloadTaskHistory,
		});
	}, [handleReloadTaskHistory, taskHistory]);

	const handlePrintButton = useCallback(() => {
		window.print();
	}, []);

	const handlePrintNeinButton = useCallback(() => {
		const { pathname, state } = getRoutePath('printNein', { taskId }, true);
		navigate(pathname, { state });
	}, [taskId, navigate]);

	const handleLoadPartnersForTaskIdAndDisplayResults = useCallback(() => {
		const { id } = taskDetail;

		dispatch(taskPartnerSearch.actions.getPartnersForTaskId(id)).then(({ body }) => {
			if (body.length > 0) {
				NiceModal.show(MODALS.task_pszp_notice, { taskId: id });
			} else {
				NiceModal.show(MODALS.lightbox_modal, {
					title: getLabelMessage('pszpNotice:noResults'),
					closeButtonLabel: getLabelMessage('common:ok'),
					showConfirmButton: false,
				});
			}
		});
	}, [dispatch, taskDetail]);

	const handleSendEmail = useCallback(() => {
		NiceModal.show(MODALS.task_copy_email);
	}, []);

	const ignoreErrorsForTaskFinish = useIgnoreErrorsWhen({
		fetchStorePath: `${fetch.constants.urls.taskFinish}.${taskId}`,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 403,
	});

	const ignoreErrorsForTakeTaskUserHasNoAcccess = useIgnoreErrorsWhen({
		fetchStorePath: fetch.constants.urls.taskTake,
		attributePath: ['metaData', 'parsedContent', 'message'],
		condition: (value) => includes(TAKE_TASK_USER_HAS_NO_ACCESS_ERR_MSG, value),
	});

	const ignoreErrorsForTakeTaskAlreadyProcessed = useIgnoreErrorsWhen({
		fetchStorePath: fetch.constants.urls.taskTake,
		attributePath: ['metaData', 'parsedContent', 'message'],
		value: TAKE_TASK_ALREADY_PROCESSED_ERR_MSG,
	});

	const ignoreErrorsForTaskDetailUserHasNoAccess = useIgnoreErrorsWhen({
		fetchStorePath: fetch.constants.urls.taskDetail,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 403,
	});

	const { statusId, taskTypeId, solverIsSubord2LoggedUser, processed, client } = taskDetail;

	const info = getCodelistPropById(taskTypeId, types, 'informative');

	const showProcessInformationButton =
		info === 1 && statusId !== 3 && (mpuProcessTask || solverIsLoggedUser || solverIsSubord2LoggedUser);

	const showTakeButton =
		(statusId !== 3 || !processed) &&
		!solverIsLoggedUser &&
		((solverIsSubord2LoggedUser && mpuAdaptTask) || mpuAssumeTaskOutofhierarchy);

	return (
		<ComponentLoader
			fetchStorePath={fetch.constants.urls.taskDetail}
			ignoreErrors={ignoreErrorsForTaskDetailUserHasNoAccess}
			renderOnlyIfLoaded
		>
			<Stack
				direction="row"
				spacing={1}
				sx={{
					justifyContent: 'flex-end',
				}}
			>
				<ProspectorButton businessIn={client?.businessIn} />
				<Button variant="text" size="small" startIcon={<TimeInterruption />} onClick={handleHistoryButton}>
					{getLabelMessage('detail:asideBarButtonHistory')}
				</Button>

				{mpuForwardTask && statusId !== 3 && (
					<Button variant="text" size="small" startIcon={<Share />} onClick={handleTaskforward}>
						{getLabelMessage('detail:asideBarButtonGet')}
					</Button>
				)}

				{showTakeButton && (
					<ComponentLoader
						fetchStorePath={fetch.constants.urls.taskTake}
						ignoreErrors={
							ignoreErrorsForTakeTaskUserHasNoAcccess || ignoreErrorsForTakeTaskAlreadyProcessed
						}
					>
						<Button
							variant="text"
							size="small"
							startIcon={<ArrowDownToBracket />}
							onClick={handleTakeButton}
						>
							{getLabelMessage('detail:asideBarButtonTake')}
						</Button>
					</ComponentLoader>
				)}

				<Button variant="text" size="small" startIcon={<Print />} onClick={handlePrintButton}>
					{getLabelMessage('detail:asideBarButtonPrint')}
				</Button>

				{taskTypeId === 20 && (
					<Button variant="text" size="small" startIcon={<ScrollRegular />} onClick={handlePrintNeinButton}>
						{getLabelMessage('detail:asideBarButtonPrintNEIN')}
					</Button>
				)}

				{taskTypeId === 17 && (
					<>
						<ModalLoader open={taskPolicyPartnerListFetchState.isFetching} />
						<Button
							variant="text"
							size="small"
							startIcon={<Wallet />}
							onClick={handleLoadPartnersForTaskIdAndDisplayResults}
						>
							{getLabelMessage('detail:asideBarButtonOpenPszpNoticeModal')}
						</Button>
					</>
				)}

				{showProcessInformationButton && (
					<ComponentLoader
						fetchStorePath={`${fetch.constants.urls.taskFinish}.${taskId}`}
						ignoreErrors={ignoreErrorsForTaskFinish}
					>
						<Button
							variant="text"
							size="small"
							startIcon={<CircleInfo />}
							onClick={handleProcessInformation}
						>
							{getLabelMessage('detail:asideBarButtonProcessInfo')}
						</Button>
					</ComponentLoader>
				)}

				{mpuSendTaskByMail && (
					<Button variant="text" size="small" startIcon={<EnvelopeOpenText />} onClick={handleSendEmail}>
						{getLabelMessage('detail:asideBarButtonEmail')}
					</Button>
				)}
			</Stack>
		</ComponentLoader>
	);
};

SubheaderToolbar.propTypes = {
	taskId: PropTypes.string,
	types: PropTypes.array,
};

export default codelistProvider(codelists.taskTypes, 'types')(SubheaderToolbar);
